/**
 * Parse a date string from backend as date-only (ignoring timezone)
 * 
 * 
 * @param dateString - ISO date string or MySQL datetime (YYYY-MM-DD or YYYY-MM-DD HH:MM:SS)
 * @returns Date object in local timezone representing the date at midnight
 * 
 */
export function parseDateOnly(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  // Extract date parts (YYYY-MM-DD)
  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  
  if (!dateMatch) return null;

  const year = parseInt(dateMatch[1], 10);
  const month = parseInt(dateMatch[2], 10) - 1; // JS months are 0-indexed
  const day = parseInt(dateMatch[3], 10);

  // Create date in local timezone (not UTC)
  return new Date(year, month, day);
}

/**
 * Format a date string from backend to display format (timezone-safe)
 * 
 * @param dateString - ISO date string or MySQL datetime
 * @param formatStr - date-fns format string (default: 'MMM dd, yyyy')
 * @returns Formatted date string or fallback text
 * 
 * @example
 * formatDateOnly("2024-03-15T00:00:00.000Z", "MMM dd, yyyy") // "Mar 15, 2024"
 * formatDateOnly("2024-03-15", "dd/MM/yyyy") // "15/03/2024"
 */
export function formatDateOnly(
  dateString: string | null | undefined,
  formatStr: string = 'MMM dd, yyyy'
): string {
  const date = parseDateOnly(dateString);
  
  if (!date || isNaN(date.getTime())) {
    return '-';
  }

  // Use date-fns or Intl.DateTimeFormat for formatting
  try {
    // Simple format for common patterns
    if (formatStr === 'MMM dd, yyyy') {
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    }
    
    if (formatStr === 'dd/MM/yyyy') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    // Fallback: use toLocaleDateString
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch (error) {
    return '-';
  }
}

/**
 * Convert a Date object to YYYY-MM-DD format for backend (date only)
 * This ensures we send only the date part, not the time
 * 
 * @param date - Date object
 * @returns Date string in YYYY-MM-DD format
 * 
 * @example
 * toDateOnlyString(new Date(2024, 2, 15)) // "2024-03-15"
 */
export function toDateOnlyString(date: Date | null | undefined): string | null {
  if (!date) return null;

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Check if a date is overdue (past today, ignoring time)
 * 
 * @param dateString - ISO date string or MySQL datetime
 * @returns true if date is in the past
 */
export function isOverdue(dateString: string | null | undefined): boolean {
  const date = parseDateOnly(dateString);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date < today;
}

/**
 * Check if a date is today (ignoring time)
 * 
 * @param dateString - ISO date string or MySQL datetime
 * @returns true if date is today
 */
export function isToday(dateString: string | null | undefined): boolean {
  const date = parseDateOnly(dateString);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date.getTime() === today.getTime();
}


export function parseDateTime(datetimeString: string | null | undefined): Date | null {
  if (!datetimeString) return null;

  try {
    let date: Date;
    
    if (typeof datetimeString === 'string' && !datetimeString.includes('Z') && !datetimeString.includes('+')) {
      date = new Date(datetimeString + 'Z');
    } else {
      date = new Date(datetimeString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  } catch (error) {
    console.warn('[dateHelpers] Failed to parse datetime:', datetimeString, error);
    return null;
  }
}


export function formatDateTime(
  datetimeString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = parseDateTime(datetimeString);
  
  if (!date) {
    return '-';
  }

  try {
    // Default format: "MMM d, yyyy h:mm a"
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    const formatOptions = options || defaultOptions;

    // Format in user's local timezone and locale
    return date.toLocaleString(undefined, formatOptions);
  } catch (error) {
    console.warn('[dateHelpers] Failed to format datetime:', datetimeString, error);
    return '-';
  }
}
